// @flow
import React from 'react';
import { useSpring } from '@react-spring/web';
import { useScroll, useWindowSize } from 'react-use';

export type ScrollDirection = 'left' | 'right';

type Options = {
  speed: number,
  distance: number,
};

const defaultOptions = {
  speed: 20,
  distance: 125,
};

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

class ContainerElement {
  scrollWidth: number;
  actualScrollPosition: number;
  offsetWidth: number;
  animationTargetScrollPosition: number;

  constructor(containerRef, animationTargetScrollPosition) {
    /* $FlowFixMe This comment suppresses an error found when upgrading Flow to
     * v0.100.0. To view the error, delete this comment and run Flow. */
    this.scrollWidth = containerRef?.scrollWidth || 0;
    /* $FlowFixMe This comment suppresses an error found when upgrading Flow to
     * v0.100.0. To view the error, delete this comment and run Flow. */
    this.actualScrollPosition = containerRef?.scrollLeft || 0;
    /* $FlowFixMe This comment suppresses an error found when upgrading Flow to
     * v0.100.0. To view the error, delete this comment and run Flow. */
    this.offsetWidth = containerRef?.offsetWidth || 0;
    this.animationTargetScrollPosition = animationTargetScrollPosition;
  }

  scrollPosition() {
    const marginOfError = 5;
    const scrollButtonLastClicked =
      Math.abs(this.actualScrollPosition - this.animationTargetScrollPosition) <
      marginOfError;
    return scrollButtonLastClicked
      ? this.animationTargetScrollPosition
      : this.actualScrollPosition;
  }

  maxScrollLeft() {
    return this.scrollWidth - this.offsetWidth;
  }

  canScrollLeft() {
    return this.scrollPosition() > 0;
  }
  canScrollRight() {
    return this.scrollPosition() < this.maxScrollLeft();
  }
}

const useRerenderConstraints = (containerRef) => {
  useWindowSize();
  useScroll({ current: containerRef });
};

export const useSideScroll = (options: Options = defaultOptions) => {
  const [containerRef, setContainerRef] =
    React.useState<?React$ElementRef<React$ElementType>>(null);

  useRerenderConstraints(containerRef);

  const [arrowButtonScrollPosition, setArrowButtonScrollPosition] =
    React.useState(0);

  const containerElement = new ContainerElement(
    containerRef,
    arrowButtonScrollPosition
  );

  const [animationScrollProps, springApi] = useSpring(
    { scrollLeft: arrowButtonScrollPosition },
    [arrowButtonScrollPosition]
  );

  const sideScroll = (direction: ScrollDirection) => {
    const scrollDistance = options.distance * (direction === 'left' ? -1 : 1);
    const newScrollPosition = clamp(
      containerElement.scrollPosition() + scrollDistance,
      0,
      containerElement.maxScrollLeft()
    );

    setArrowButtonScrollPosition(newScrollPosition);
    springApi.start({
      reset: true,
      to: { scrollLeft: newScrollPosition },
      from: { scrollLeft: containerElement.scrollPosition() },
    });
  };

  return {
    setContainerRef,
    canScrollLeft: containerElement.canScrollLeft(),
    canScrollRight: containerElement.canScrollRight(),
    scrollProps: animationScrollProps,
    sideScroll,
  };
};
